import { type BasePaginationData } from "shared/components/base/BasePagination";
import { type streamTypes } from "shared/constants";
import { type FilterSetOptionsFilters } from "shared/helpers/FilterSetFilters";
import type { MediumContentFlags, MediumName } from "shared/helpers/media";
import { type Source } from "shared/helpers/sourceFilters";
import { type StreamOptionsFilters } from "shared/helpers/StreamFilters";
import type { Stream as StreamResource } from "shared/resources";
import type { StreamNotification } from "shared/services/streamNotification";
import type { EmailDeliveryOptionsPayload } from "shared/services/streamNotificationSpecification";
import type { MentionType, SocialMentionType } from "shared/types/mentions";

import type { ConvertKeysToSnakeCase, Nullable } from "./utility";

export * from "./querySnippets";
export * from "./utility";

export interface Origin {
  origin_id: number;
  origin_keyword?: string;
  origin_type: string;
}

export interface Target {
  target_id: number;
  target_type: string;
}

export interface BaseSentimentRating extends Origin, Target {
  created_at: string;
  id: number;
  origin_id: number;
  origin_type: string;
  sentiment: number;
  target_id: number;
  target_type: string;
  updated_at: string;
  user: {
    id: number;
    name: string;
  };
}

export interface SentimentRating extends Partial<BaseSentimentRating> {
  sentiment: number;
}

export interface MediaAndTranscriptRequestQuery {
  id: number;
  isMediaItems?: boolean;
  isTranscriptRequest?: boolean;
  label: string;
  position: number;
}

export interface StreamGroupStream {
  archived_at: Nullable<string>;
  color: string;
  id: number;
  label: string;
  shared: boolean;
}

export type StreamType = (typeof streamTypes)[keyof typeof streamTypes];

export interface StreamGroup {
  can_modify: boolean;
  color: string;
  id: number;
  label: string;
  locked: boolean;
  organisation_id: number;
  organisation_team_id?: Nullable<number>;
  organisation_team_ids?: number[];
  organisation_teams?: { id: number; name: string }[];
  position: number;
  shared: boolean;
  shared_groups: {
    access_level: string;
    can_edit: boolean;
    user_id: number;
  }[];
  slug: string;
  social_bookmarks_stream: boolean;
  stream_type: StreamType;
  streams: StreamGroupStream[];
  user?: {
    id: number;
    name: string;
  };
  user_accessibles?: {
    access_level: string;
    accessible_id: number;
    accessible_type: string;
    user_id: number;
  }[];
  user_id: Nullable<number>;
}

export interface FilterTargetLocation extends Location {
  aliases: string[];
  parent_hierarchy: number[];
}

type FilterTarget = FilterTargetLocation | Category;

export interface StreamFilter {
  action: string;
  filter: FilterTarget;
  id?: number;
  name: string;
  scope: string;
  target_id: number;
  target_type: "Category" | "Location";
}

export interface StreamNotificationFilter {
  action: "exclude" | "include";
  filter_key?: string;
  id?: number;
  scope: string;
  target_id: number;
  target_type: "Category" | "Location" | "SourceGroup";
}

export interface StreamNotificationSource {
  action: "exclude" | "include";
  filter_key?: string;
  id?: number;
  target_id: number;
  target_type: string;
}

export type UserAccessLevel =
  | "collaborator"
  | "read_only"
  | "read_write"
  | "owner";

export interface StreamUser {
  access_level: UserAccessLevel;
  can_edit: boolean;
  id: number;
}

export interface Stream extends Partial<
  Omit<
    ConvertKeysToSnakeCase<StreamResource>,
    | "id"
    | "user"
    | "group"
    | "filters"
    | "sources"
    | "stream_notifications"
    | "streams_users"
    | "typed_attributes"
  >
> {
  auto_refresh: boolean;
  can_modify: boolean;
  color: Nullable<string>;
  count?: number;
  dashboard_enabled: boolean;
  dashboard_only: boolean;
  default_sentiment_rating: number;
  enabled_media: MediumName[];
  expiration_dismissed?: boolean;
  filter_set_ids?: number[];
  filter_sets?: StreamFilterSet[];
  filters?: StreamFilter[];
  group: {
    color: string;
    id: number;
    label: string;
    organisation_team_id: Nullable<number>;
    position: number;
    slug: string;
  };
  group_id: number;
  has_streams_user: boolean;
  hydration_status: Nullable<number>;
  id: number;
  label: string;
  locked: boolean;
  onlyLastSeenUpdated?: boolean;
  options?: Nullable<StreamOptionsFilters>;
  position: Nullable<number>;
  primary: Nullable<boolean>;
  refreshAt?: number;
  refreshed_at: Nullable<number | string>;
  report_enabled: boolean;
  resetByGroup?: boolean;
  sentiment_mode_enabled: boolean;
  shared: boolean;
  shared_with_me: boolean;
  skipReload?: boolean;
  slug: string;
  social_bookmarks_stream: boolean;
  social_impact_threshold: number;
  social_only_verified: boolean;
  sources?: Source[];
  team_ids: Nullable<number[]>;
  type: StreamType;
  user?: {
    id: number;
    name: string;
  };
  user_id: number;
  users?: StreamUser[];
}

export type Uuid = `${string}-${string}-${string}-${string}-${string}`;

export type SortingOrder = "asc" | "desc";

export interface SortOption<T = string> {
  direction: SortingOrder;
  label: string;
  value: T;
}

export enum DataLoadingWrapperState {
  Done = "done",
  Error = "error",
  Loading = "loading",
  NoData = "noData",
}

export type SelectAllId = number | string;

export interface AsyncData<T> {
  data: T;
  initialLoad?: boolean;
  isLoading: boolean;
  pagination?: Required<BasePaginationData>;
  query?: Record<string, any>;
  selectAllIds?: SelectAllId[];
  sortBy?: SortOption;
}

export interface Location {
  country_code: Nullable<string>;
  id: number;
  latitude: string;
  longitude: string;
  name: string;
  parent_id: Nullable<number>;
  position: Nullable<number>;
  time_zone: string;
  type: string;
  visible: boolean;
}

export interface Category {
  id: number;
  name: string;
  social?: boolean;
}

export interface FilterSetGroup {
  id?: number;
  name: string;
}

export interface StreamFilterSet {
  id: number;
  label: string;
}

export interface UserAccessible {
  access_level: UserAccessLevel;
  accessible_id: number;
  accessible_type: string;
  user_id: number;
}

interface Owner {
  first_name: string;
  id: number;
  last_name: string;
}

export interface FilterSet extends Required<MediumContentFlags> {
  description?: string;
  enabled_media: MediumName[];
  filter_set_group?: FilterSetGroup;
  filters?: StreamFilter[];
  id?: number;
  label: string;
  options: FilterSetOptionsFilters;
  organisation_id: number;
  organisation_team_ids?: number[];
  owner?: Owner;
  selected_magazine_content?: MentionType[];
  selected_online_content?: MentionType[];
  selected_podcast_content?: MentionType[];
  selected_print_content?: MentionType[];
  selected_radio_content?: MentionType[];
  selected_social_content?: SocialMentionType[];
  selected_tv_content?: MentionType[];
  social_only_verified?: boolean;
  sources?: Source[];
  stream_ids?: number[];
  user_accessibles?: UserAccessible[];
  user_id: number;
}

export enum AppSection {
  AI_VISIBILITY = "ai_visibility",
  MONITORING = "monitoring",
  OUTREACH = "outreach",
  SOCIAL = "social",
}

export enum QuerySnippetType {
  MONITORING = AppSection.MONITORING,
  SOCIAL = AppSection.SOCIAL,
}

export interface SharedWithUsers {
  access_level: UserAccessLevel;
  can_edit?: boolean;
  is_dirty?: boolean;
  label?: string;
  name?: string;
  removed?: boolean;
  user_id: number;
}

export interface SlackChannel {
  label: string;
  value: string;
}

export type EmailDeliverySender =
  | "organisation"
  | "user"
  | "organisation_domain"
  | "custom";

export interface EmailDeliveryOptions {
  customSender: string;
  emailDomainId?: Nullable<number>;
  recipients: string[];
  sender: EmailDeliverySender;
  senderEmail: string;
  senderName: string;
  userId: number;
}

export type StreamNotificationWithSpecification = StreamNotification & {
  email_delivery_options: EmailDeliveryOptionsPayload;
};

export type Integrations = "slack" | "microsoft_teams";

export interface IntegrationChannel {
  id: number;
  name: string;
}
